import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus } from "lucide-react";
import PartnerGate from "./Gate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, StatusBadge } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

export default function Team() {
  const [team, setTeam] = useState([]);
  const [memberModal, setMemberModal] = useState(false);
  const [member, setMember] = useState({ name: "", email: "", password: "" });
  const [invited, setInvited] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/partner/team").then(({ data }) => setTeam(data.members)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const inviteRecruiter = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/partner/team", {
        name: member.name, email: member.email, password: member.password || undefined,
      });
      setInvited(data);
      setMember({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setRecruiterStatus = async (id, status) => {
    try {
      await api.post(`/partner/team/${id}/status`, { status });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeRecruiter = async (id) => {
    try {
      await api.delete(`/partner/team/${id}`);
      toast.success("Recruiter removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <PartnerGate>
      {(overview) => (
        <div data-testid="partner-team">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setInvited(null); setMemberModal(true); }} className="rounded-full bg-[#0B1B3D]" data-testid="invite-recruiter-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Recruiter
            </Button>
          </div>
          {overview.is_member ? (
            <EmptyState title="Owner access required" description="Only the franchise owner can manage sub-recruiters." testId="team-member-blocked" />
          ) : team.length === 0 ? (
            <EmptyState title="No recruiters yet" description="Invite sub-recruiters to submit candidates under your franchise. Their activity is attributed to them in every submission." testId="team-empty" />
          ) : (
            <div className="bg-white border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-sm" data-testid="team-table">
                <thead className="bg-[#F8FAFC] border-b border-border/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Name</th>
                    <th className="px-5 py-3.5 font-semibold">Email</th>
                    <th className="px-5 py-3.5 font-semibold">Submissions</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {team.map((m) => (
                    <tr key={m.id} className="border-b border-border/40" data-testid={`member-${m.id}`}>
                      <td className="px-5 py-4 font-semibold">{m.name}</td>
                      <td className="px-5 py-4 text-xs">{m.email}</td>
                      <td className="px-5 py-4">{m.submissions ?? 0}</td>
                      <td className="px-5 py-4"><StatusBadge status={m.status} /></td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          {m.status === "active" ? (
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRecruiterStatus(m.id, "suspended")} data-testid={`suspend-${m.id}`}>Suspend</Button>
                          ) : (
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRecruiterStatus(m.id, "active")} data-testid={`activate-${m.id}`}>Activate</Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-600 rounded-full" onClick={() => removeRecruiter(m.id)} data-testid={`remove-${m.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Dialog open={memberModal} onOpenChange={setMemberModal}>
            <DialogContent aria-describedby={undefined} data-testid="recruiter-dialog">
              <DialogHeader><DialogTitle>Invite sub-recruiter</DialogTitle></DialogHeader>
              {invited ? (
                <div className="space-y-4" data-testid="recruiter-invited">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-semibold text-[#0B1B3D]">{invited.name}</span> can now sign in and submit candidates under your franchise.
                  </p>
                  <div className="bg-[#F8FAFC] border border-border/60 rounded-lg px-4 py-3 text-sm">
                    <p>Email: <span className="font-mono font-semibold">{invited.email}</span></p>
                    <p className="mt-1">Temporary password: <span className="font-mono font-semibold" data-testid="recruiter-temp-password">{invited.temporary_password}</span></p>
                  </div>
                  <p className="text-xs text-muted-foreground">Share these credentials securely — the password is shown only once.</p>
                  <Button onClick={() => setMemberModal(false)} className="w-full rounded-full bg-[#0B1B3D]" data-testid="recruiter-done-btn">Done</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div><Label>Name *</Label><Input value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} className="mt-1.5" data-testid="recruiter-name" /></div>
                  <div><Label>Email *</Label><Input type="email" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} className="mt-1.5" data-testid="recruiter-email" /></div>
                  <div><Label>Password (optional — auto-generated if blank)</Label><Input value={member.password} onChange={(e) => setMember({ ...member, password: e.target.value })} className="mt-1.5" data-testid="recruiter-password" /></div>
                  <Button onClick={inviteRecruiter} disabled={busy || !member.name || !member.email} className="w-full rounded-full bg-[#0B1B3D]" data-testid="recruiter-invite-submit-btn">
                    {busy ? "Inviting…" : "Invite Recruiter"}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}
    </PartnerGate>
  );
}
